/** @module popUp */

import { liveEvents } from '../../../../../assets/js/application/index.js';

/**
 * Initializes pop-up functionalities.
 * Should not be referenced directly.
 *
 * @param {object} [options={}] - Options for the pop-up.
 * @param {boolean} [options.blockScrolling=false] - Whether the site's scrolling is blocked while the pop-up is open.
 *
 * @private
 * @see {@link popUp}
 */
export async function initPopUp({ blockScrolling = false }) {
    const events = await liveEvents(),
        storageKey = 'spwk_popups';

    function getCorrectStorage(hiddenForDays) {
        if (hiddenForDays !== 0 && 'localStorage' in window) {
            return window.localStorage;
        } else if (hiddenForDays === 0 && 'sessionStorage' in window) {
            return window.sessionStorage;
        }

        return null;
    }

    function eventuallyPersistClosingTime(popUp) {
        try {
            if ('hiddenForDays' in popUp.dataset) {
                const hiddenForDays = parseInt(popUp.dataset.hiddenForDays, 10) || 0,
                    storage = getCorrectStorage(hiddenForDays),
                    popUpStorageKey = popUp.id,
                    settings = storage ? JSON.parse(storage.getItem(storageKey)) || {} : null;

                if (storage === null) {
                    return;
                }

                settings[popUpStorageKey] = (popUp.dataset.version || '1') + ':' + Date.now();

                storage.setItem(storageKey, JSON.stringify(settings));
            }
        } catch (e) {
            //
        }
    }

    function shouldOpenPopUp(popUp) {
        if ('hiddenForDays' in popUp.dataset && 'localStorage' in window) {
            try {
                const popUpStorageKey = popUp.id,
                    hiddenForDays = parseInt(popUp.dataset.hiddenForDays, 10),
                    storage = getCorrectStorage(hiddenForDays),
                    storageSetting = storage ? JSON.parse(storage.getItem(storageKey)) || {} : null,
                    hiddenDaysInMillis = hiddenForDays * 24 * 60 * 60 * 1000;

                if (storage === null) {
                    return true;
                }

                let lastClosedDate;

                if (popUpStorageKey in storageSetting) {
                    let value = storageSetting[popUpStorageKey].split(':'),
                        version = value.length > 1 ? value[0] : '1';

                    if (version !== popUp.dataset.version) {
                        // if the pop-up got a new version, do show it regardless
                        return true;
                    }

                    if (hiddenForDays === 0 || hiddenForDays === -1) {
                        // 0 = only show once per session
                        return false;
                    }

                    lastClosedDate = version.length > 1 ? version[1] : version[0];
                } else {
                    lastClosedDate = Date.now();
                }

                if (lastClosedDate + hiddenDaysInMillis > new Date()) {
                    return false;
                }
            } catch (e) {
                //
            }
        }

        return true;
    }

    function onClose(event) {
        const popUp = event.target,
            popUpId = popUp.id,
            trigger = document.querySelector(`[data-pop-up-open="${popUpId}"]`);

        if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
            trigger.focus(); // set focus back to button that opened the pop-up
        }

        if (blockScrolling) {
            document.documentElement.style.overflow = '';
        }

        eventuallyPersistClosingTime(popUp);
    }

    function open(dialog, delay = 0) {
        if (delay > 0) {
            setTimeout(() => {
                open(dialog);
            }, delay);

            return;
        }

        if (blockScrolling) {
            document.documentElement.style.overflow = 'hidden';
        }

        if ('modal' in dialog.dataset) {
            dialog.showModal();
        } else {
            dialog.show();
        }
    }

    function onTriggerClick(event, selector) {
        const trigger = event.target.closest(selector),
            dialog = document.getElementById(trigger.dataset.popUpOpen);

        if (!dialog) {
            return;
        }

        open(dialog);

        trigger.setAttribute('aria-expanded', 'true');
    }

    function onBackdropClick(event) {
        const clickBlock = event.target.closest('[data-pop-up-click-block]'),
            dialog = event.target.closest('dialog');

        if (dialog && !clickBlock) {
            event.stopImmediatePropagation();

            dialog.close();
        }
    }

    function close(event) {
        const dialog = event.target.closest('dialog');

        if (dialog) {
            dialog.close();
        }
    }

    events
        .addEventListener('[data-pop-up-open]', 'click', onTriggerClick, { passive: true })
        .addEventListener('[data-pop-up]', 'click', onBackdropClick, { passive: true, capture: true })
        .addEventListener('[data-pop-up-close]', 'click', close, { passive: true });

    document.querySelectorAll('[data-pop-up]').forEach((popUp) => {
        // this event does not bubble, so liveEvents cannot be used
        popUp.addEventListener('close', onClose);

        if ('delay' in popUp.dataset) {
            if (shouldOpenPopUp(popUp)) {
                const delay = (parseInt(popUp.dataset.delay, 10) || 0) * 1000;

                if (document.readyState !== 'loading') {
                    open(popUp, delay);
                } else {
                    document.addEventListener(
                        'readystatechange',
                        () => {
                            open(popUp, delay);
                        },
                        { once: true, passive: true },
                    );
                }
            }
        }
    });
}
